import { CircularProgress, NativeSelect } from '@material-ui/core';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import React from 'react';

import { DashboardCardValueType, IDashboardCard } from '~/src/dash/components/cards/common';
import { CHART_COLORS, IDashboardChartMetric } from '~/src/dash/components/charts/common';
import { FONT_WEIGHT } from '~/src/dash/components/text/common';
import { Col, Cols, Div, Row, Rows, spacing, Text } from '~/src/dash/publicaUI';
import { convertLargeNumbersToRoundedString } from '~/src/dash/utils/basic';
import { publicaColors } from '~/src/dash/utils/colors';
import { LoadingState, LoadingStateLoading } from '~/src/dash/utils/loading-states';
import { useMobileStore } from '~/src/dash/views/mobile/contexts';

const ARROW_STYLE = { fontSize: '13px', paddingBottom: '1px' };
const POSITIVE_COLOR = publicaColors.jungleGreen;
const NEGATIVE_COLOR = publicaColors.lightRed;
const NEUTRAL_COLOR = publicaColors.gray;

const getTrendColor = (value: number, isInverseTrend?: boolean) => {
  switch (true) {
    case value > 0:
      return isInverseTrend ? NEGATIVE_COLOR : POSITIVE_COLOR;
    case value < 0:
      return isInverseTrend ? POSITIVE_COLOR : NEGATIVE_COLOR;
    case value === 0:
    default:
      return NEUTRAL_COLOR;
  }
};

const displayArrow = (value: number, color: string) => {
  switch (true) {
    case value > 0:
      return <ArrowUpwardIcon style={{ ...ARROW_STYLE, color }} />;
    case value < 0:
      return <ArrowDownwardIcon style={{ ...ARROW_STYLE, color }} />;
    default:
      return null;
  }
};

const formatCardValue = (
  value: number | null,
  valueType: DashboardCardValueType,
  currencySymbol: string,
) => {
  if (!value && value !== 0) {
    return 'N/A';
  }
  switch (valueType) {
    case 'integer':
      return convertLargeNumbersToRoundedString(value);
    case 'percent':
      return `${value.toFixed(2)}%`;
    case 'currency':
      return `${currencySymbol} ${convertLargeNumbersToRoundedString(value, 2)}`;
    case 'currency_rate':
      return `${currencySymbol} ${value.toFixed(value > 1 ? 2 : 3)}`;
    default:
      return value.toString();
  }
};

const formatTrend = (value: number | null, isInverseTrend?: boolean) => {
  if (!value && value !== 0) {
    return (
      <Text small bold color={publicaColors.gray} disableSelect>
        N/A
      </Text>
    );
  }
  const color = getTrendColor(value, isInverseTrend);
  return (
    <Cols gap='4px' style={{ alignItems: 'center' }}>
      <Col displayFlex width={16}>
        {displayArrow(value, color)}
      </Col>
      <Col>
        <Text small bold color={color} disableSelect>
          {`${Math.abs(value).toFixed(2)}%`}
        </Text>
      </Col>
    </Cols>
  );
};

interface IMobileCardProps {
  cardMetrics: Record<string, any>;
  availableMetrics: Record<string, IDashboardCard>;
  selectedMetric: IDashboardCard;
  index: number;
  isCompareEnabled?: boolean;
  isDisplayedOnChart?: boolean;
  handleOnClickCard: (metricKey: string, index: number) => void;
  handleOnChangeCardMetric: (metricKey: string, index: number, isDisplayedOnChart: boolean) => void;
  loadState: LoadingState;
}

const MobileCard = ({
  loadState,
  cardMetrics,
  availableMetrics,
  selectedMetric,
  index,
  isCompareEnabled,
  isDisplayedOnChart = false,
  handleOnClickCard,
  handleOnChangeCardMetric,
}: IMobileCardProps) => {
  const { isInverseTrend, responseKey, trendKey, valueType } = selectedMetric;
  const { currencySymbol } = useMobileStore();

  return (
    <Div
      onClick={() => handleOnClickCard(selectedMetric.key, index)}
      style={{
        backgroundColor: isDisplayedOnChart ? publicaColors.lightGray : 'inherit',
        border: `1px solid ${
          isDisplayedOnChart ? publicaColors.mediumGray : publicaColors.lightGray
        }`,
        borderRadius: '4px',
        padding: '10px 16px',
      }}
    >
      <Rows>
        <Row noPadding>
          <Cols style={{ alignItems: 'center' }}>
            {isDisplayedOnChart && (
              <Col displayFlex paddingRight={spacing.small}>
                <Text small color={CHART_COLORS[index % 6][0]}>
                  ❙
                </Text>
              </Col>
            )}
            <Col flex>
              <NativeSelect
                value={selectedMetric.key}
                fullWidth
                disableUnderline
                onChange={(e) => {
                  handleOnChangeCardMetric(e.currentTarget.value, index, isDisplayedOnChart);
                }}
                inputProps={{
                  style: {
                    border: 'none',
                    fontWeight: FONT_WEIGHT.bold,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    padding: '0px 24px 0px 0px',
                  },
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {Object.values(availableMetrics).map((metric) => {
                  return <option value={metric.key}>{metric.label}</option>;
                })}
              </NativeSelect>
            </Col>
          </Cols>
        </Row>
        <Row noPadding>
          {loadState !== LoadingStateLoading ? (
            <Text extraLarge bold disableSelect>
              {formatCardValue(cardMetrics[responseKey], valueType, currencySymbol)}
            </Text>
          ) : (
            <div style={{ padding: '8px 0px' }}>
              <CircularProgress size={14} />
            </div>
          )}
        </Row>
        {loadState !== LoadingStateLoading && isCompareEnabled && (
          <Row noPadding>{formatTrend(cardMetrics[trendKey], isInverseTrend)}</Row>
        )}
      </Rows>
    </Div>
  );
};

interface IMobileCardsProps {
  availableMetrics: Record<string, IDashboardCard>; // all possible options for the dropdown
  displayedMetricKeys: string[]; // displayed metrics in order
  cardMetrics: Record<string, any>;
  loadState: LoadingState;
  isCompareEnabled?: boolean;
  activeChartMetrics: IDashboardChartMetric[];
  handleOnClickCard: (metricKey: string, index: number) => void;
  handleOnChangeCardMetric: (metricKey: string, index: number) => void;
}

export const MobileCards = ({
  availableMetrics,
  cardMetrics,
  displayedMetricKeys,
  loadState,
  isCompareEnabled,
  activeChartMetrics,
  handleOnClickCard,
  handleOnChangeCardMetric,
}: IMobileCardsProps) => {
  return (
    <Cols wrap gap={spacing.base} style={{ rowGap: spacing.base }}>
      {displayedMetricKeys.map((key, i) => {
        const metric = availableMetrics[key];
        return (
          <Col
            flex
            style={{
              minWidth: '45%',
            }}
          >
            <MobileCard
              loadState={loadState}
              cardMetrics={cardMetrics}
              availableMetrics={availableMetrics}
              selectedMetric={availableMetrics[metric.key]}
              index={i}
              isCompareEnabled={isCompareEnabled}
              isDisplayedOnChart={
                !!activeChartMetrics.find((activeMetric) => activeMetric.key === metric.key)
              }
              handleOnClickCard={handleOnClickCard}
              handleOnChangeCardMetric={handleOnChangeCardMetric}
            />
          </Col>
        );
      })}
    </Cols>
  );
};
